import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Nav, Tab } from "react-bootstrap";
import SignatureCanvas from "react-signature-canvas";
import { toast } from "react-toastify";
import { getInitials } from "../../../../../utilities/stringUtils";

interface EnterNameStepProps {
  onContinue: (signature: {name: string, initials: string}) => void;
}

const EnterNameStep: React.FC<EnterNameStepProps> = ({ onContinue }) => {
  const sigPadRef = useRef<SignatureCanvas | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null); // Reference to the parent container
  const [canvasDimensions, setCanvasDimensions] = useState({
    width: 0,
    height: 0
  });

  const [typedName, setTypedName] = useState("");
  const [typedInitials, setTypedInitials] = useState("");
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("draw");

  const handleContinue = () => {
    if (activeTab === 'type' && typedName.trim()) {
      onContinue({name: typedName.trim(), initials: getInitials(typedName.trim())});
    } else if (activeTab === 'draw' && signatureData && typedInitials.trim()) {
      onContinue({name: signatureData, initials: typedInitials.trim()});
    } else {
      if(activeTab === 'draw' && !typedInitials.trim()){
        toast.warning("Please provide your initials to continue.");
        return;
      }
      toast.warning("Please provide your signature to continue.");
    }
  };

  const onSignatureEnd = () => {
    const canvas = sigPadRef.current?.getCanvas();
    if (canvas) {
      if (sigPadRef.current?.isEmpty()) {
        setSignatureData(null);
      } else {
        setSignatureData(canvas.toDataURL("image/png"));
      }
    }
  };

  // Calculate dimensions on mount and resize
  useEffect(() => {
    const handleResize = () => {
      if (wrapperRef.current) {
        // Get the actual computed width of the parent container
        const newWidth = wrapperRef.current.offsetWidth;

        setCanvasDimensions({
          // Set the internal canvas width to the parent's actual width
          width: newWidth,
          // Set a fixed height or a responsive height (e.g., 20% of the width)
          height: 150,
        });
      }
    };

    handleResize(); // Initial size calculation
    window.addEventListener('resize', handleResize); // Add listener for responsiveness

    return () => window.removeEventListener('resize', handleResize); // Clean up
  }, []);

  const handleClear = () => {
    sigPadRef.current?.clear();
    setSignatureData(null);
  }

  return (
    <div className="bg-white rounded-lg shadow-xl flex flex-col max-h-[80vh] w-full">
      <div className="top border_tittle p-6">
        <div className="top_bar_tittle">
          <h2 className="text-xl font-bold">Adopt Your Signature</h2>
        </div>
      </div>
      <div className="px-6 py-4 w-full">
        <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'draw')}>
          <Nav variant="pills" className="common_tab mb-4">
            <Nav.Item>
              <Nav.Link eventKey="draw">Draw</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="type">Type</Nav.Link>
            </Nav.Item>
          </Nav>
          <Tab.Content>
            <Tab.Pane eventKey="draw">
              <Form.Group>
                <Form.Label>Draw your signature below</Form.Label>
                <div ref={wrapperRef} style={{ width: '100%' }}>
                  <SignatureCanvas
                    ref={sigPadRef}
                    penColor="black"
                    onEnd={onSignatureEnd}
                    canvasProps={{
                      width: canvasDimensions.width,
                      height: canvasDimensions.height,

                      // 2. CSS SIZING (use percentages/fixed pixels for display)
                      style: {
                        width: "100%",
                        height: `${canvasDimensions.height}px`
                      },
                      className: "bg-gray-50 border border-gray-300 rounded-md",
                    }}
                  />
                </div>
              </Form.Group>
              <div className="text-right mt-2">
                <button onClick={handleClear} className="highlight_txt text-sm">
                  Clear
                </button>
              </div>
              <Form.Group className="mt-3">
                <Form.Label>Type your initials</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="JD"
                  value={typedInitials}
                  onChange={(e) => setTypedInitials(e.target.value)}
                />
              </Form.Group>
            </Tab.Pane>
            <Tab.Pane eventKey="type">
              <Form.Group>
                <Form.Label>Type your full name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="John Hancock"
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                  className="signature-font-rendered" // You might need to style this class
                />
              </Form.Group>
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </div>
      <div className="p-6 border-t mt-auto">
        <p className="text-xs text-gray-500 mb-4">
          By clicking Adopt, you are agreeing that the signature and initials will be the electronic representation of your signature and initials for all purposes when you use them on documents, including legally binding contracts.
        </p>
        <Button variant="primary" className="w-full" onClick={handleContinue}>
          Adopt
        </Button>
      </div>
    </div>
  );
};

export default EnterNameStep;